import { useEffect, useState } from "react";
import { Bell, X, Gift, Trophy } from "lucide-react";
import { useAuth } from "@/auth";
import { useLang } from "@/i18n";
import { api } from "@/lib/api";

const T = { es: { close: "Cerrar", more: "más", dismissAll: "Descartar todos" }, en: { close: "Close", more: "more", dismissAll: "Dismiss all" } };

// Shows unread admin notifications (e.g. "You're a weekly winner!") as a pop-up
// to a signed-in player, one at a time, marking each read on dismiss.
export function NotificationPopup() {
  const { user } = useAuth();
  const { lang } = useLang();
  const t = T[lang] || T.es;
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (!user) { setItems([]); return; }
    api.myNotifications().then((r) => setItems(r.data || [])).catch(() => setItems([]));
  }, [user]);

  if (!user || items.length === 0) return null;
  const n = items[0];

  const dismiss = async () => {
    try { await api.readNotification(n.id); } catch {}
    setItems((arr) => arr.slice(1));
  };
  const dismissAll = async () => {
    setItems([]);
    try { await api.readAllNotifications(); } catch {}
  };

  const useEn = lang === "en";
  const nTitle = (useEn && n.title_en) ? n.title_en : n.title;
  const nBody = (useEn && n.body_en) ? n.body_en : n.body;
  const title = nTitle || "";
  const isGrant = title.includes("🎁") || /personaje|character/i.test(title);
  const isWin = title.includes("🏆");
  const Icon = isGrant ? Gift : isWin ? Trophy : Bell;

  return (
    <div className="fixed inset-0 z-[98] overflow-y-auto overscroll-contain flex items-start justify-center bg-black/80 p-4" data-testid="notification-popup" onClick={dismiss}>
      <div className="pixel-card bg-[#141414] w-full max-w-sm p-6 relative winner-pop text-center my-auto" onClick={(e) => e.stopPropagation()}>
        <button data-testid="notification-dismiss" onClick={dismiss} aria-label={t.close} className="absolute top-3 right-3 text-white/50 hover:text-white"><X size={20} /></button>
        <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-3 ${isGrant ? "bg-[#c44dff] animate-bounce" : "bg-[#FFB300]"}`}>
          <Icon size={30} className="text-black" strokeWidth={2.5} />
        </div>
        <h3 className="font-pixel text-sm text-[#FFEB3B] pixel-stroke leading-snug">{nTitle}</h3>
        <p className="font-vt text-xl text-white/90 mt-3 whitespace-pre-wrap leading-snug">{nBody}</p>
        {items.length > 1 && <p className="font-vt text-sm text-white/40 mt-4">+{items.length - 1} {t.more}</p>}
        <div className="flex items-center justify-center gap-2 mt-5">
          <button data-testid="notification-ok" onClick={dismiss} className="pixel-btn bg-[#FFB300] text-black px-8 py-2.5 text-[11px]">OK</button>
          {items.length > 1 && (
            <button data-testid="notification-dismiss-all" onClick={dismissAll} className="pixel-btn bg-[#333] text-white px-5 py-2.5 text-[11px]">{t.dismissAll}</button>
          )}
        </div>
      </div>
    </div>
  );
}
